import LetterIcon from '@static/images/icons/letter.svg';
import { Button, Input } from '@toggle/design-system';
import React, { ChangeEvent, FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { authPaths } from '~/routes/app-paths';

import * as AuthSharedStyles from '../auth-shared-styles/AuthSharedStyles.styles';
import styles from './ForgotPasswordView.module.scss';
import { useForgotPasswordState } from './useForgotPasswordState';

export const ForgotPasswordView = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const {
    email,
    clearError,
    validateEmail,
    updateEmail,
    postReqResetPassword,
    requestState: { isPending, isSuccess },
    errorText,
  } = useForgotPasswordState();
  const [successTabView, setSuccessTabView] = useState(false);

  const onChangeEmail = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    updateEmail(value);
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (errorText || isPending) {
      return;
    }

    //Field validation
    const isValid = validateEmail(email);

    if (!isValid) {
      return;
    }

    setSuccessTabView(true);
    postReqResetPassword(email);
  };

  const loginRedirect = () => {
    navigate(authPaths.login);
  };

  const isLinkSent = isSuccess && successTabView;

  return (
    <AuthSharedStyles.Layout>
      <div className={styles.wrapper}>
        <div className={styles.container}>
          {!isLinkSent && <AuthSharedStyles.Logo alt="logo" title="logo" />}
          <form
            className={styles.form}
            onSubmit={onSubmit}
            data-testid="forgot-password"
          >
            {isLinkSent ? (
              <AuthSharedStyles.WrapperWithBg
                className={styles.emailSentWrapper}
              >
                <LetterIcon className={styles.letterSvg} />
                <h1 className={styles.successTitle} data-testid="success-title">
                  {t('auth:forgotResetPassword.checkInbox')}
                </h1>
                <p
                  className={styles.successText}
                  data-testid="success-description"
                >
                  {t('auth:forgotResetPassword.detailedNote')}
                </p>
                <div className={styles.successEmailWrapper}>{email}</div>
                <Button
                  className={styles.backToLoginButton}
                  data-testid="back-to-login"
                  label={t('auth:forgotResetPassword.backToLogin')}
                  onClick={loginRedirect}
                  variant="tertiary"
                  size="large"
                />
              </AuthSharedStyles.WrapperWithBg>
            ) : (
              <AuthSharedStyles.WrapperWithBg
                className={styles.forgotPassWrapper}
              >
                <AuthSharedStyles.Heading>
                  {t('auth:forgotResetPassword.heading')}
                </AuthSharedStyles.Heading>
                <p className={styles.description}>
                  {t('auth:forgotResetPassword.provideEmailNote')}
                </p>
                <Input
                  id="email-input"
                  name="email-input"
                  type="text"
                  variant="outline"
                  onChange={onChangeEmail}
                  onFocus={clearError}
                  value={email}
                  hasError={!!errorText}
                  label={t('auth:forgotResetPassword.emailLabel')}
                  errorText={errorText}
                  data-testid="email-input"
                />
                <AuthSharedStyles.CenterContent>
                  <AuthSharedStyles.SendButtonWrapper>
                    <Button
                      type="submit"
                      fullWidth
                      label={t('auth:forgotResetPassword.sendResetLink')}
                      isLoading={isPending}
                      data-testid="submit-button"
                      size="large"
                    />
                  </AuthSharedStyles.SendButtonWrapper>
                  <AuthSharedStyles.CancelButton
                    to={authPaths.login}
                    data-testid="cancel-button"
                  >
                    {t('auth:forgotResetPassword.cancelButton')}
                  </AuthSharedStyles.CancelButton>
                </AuthSharedStyles.CenterContent>
              </AuthSharedStyles.WrapperWithBg>
            )}
          </form>
        </div>
      </div>
    </AuthSharedStyles.Layout>
  );
};
